import { CheckCircle, Info, X, XCircle } from "lucide-react";
import type React from "react";
import { createContext, useContext, useState } from "react";
import "./Toast.css";

type ToastType = "success" | "error" | "info";

interface Toast {
	id: string;
	message: string;
	type: ToastType;
}

interface ToastContextType {
	showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
	const [toasts, setToasts] = useState<Toast[]>([]);

	const showToast = (message: string, type: ToastType = "info") => {
		const id = Math.random().toString(36).substr(2, 9);
		setToasts((prev) => [...prev, { id, message, type }]);

		// Auto-remove after 4 seconds
		setTimeout(() => {
			removeToast(id);
		}, 4000);
	};

	const removeToast = (id: string) => {
		setToasts((prev) => prev.filter((t) => t.id !== id));
	};

	return (
		<ToastContext.Provider value={{ showToast }}>
			{children}
			<div className="toast-container">
				{toasts.map((toast) => (
					<div key={toast.id} className={`toast toast-${toast.type}`}>
						<div className="toast-icon">
							{toast.type === "success" && <CheckCircle size={18} />}
							{toast.type === "error" && <XCircle size={18} />}
							{toast.type === "info" && <Info size={18} />}
						</div>
						<div className="toast-content">{toast.message}</div>
						<button
							type="button"
							className="toast-close"
							onClick={() => removeToast(toast.id)}
						>
							<X size={14} />
						</button>
					</div>
				))}
			</div>
		</ToastContext.Provider>
	);
};

export const useToast = () => {
	const context = useContext(ToastContext);
	if (!context) {
		throw new Error("useToast must be used within a ToastProvider");
	}
	return context;
};
